import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  useCfOrderDetail,
  useCfOrderStatusCounts,
  useCfOrdersList,
} from "@/modules/cf-order/presentation/api/queries";
import type { CfOrderListItem } from "@/modules/cf-order/presentation/api/types";
import { formatKip } from "@/modules/cf-feed/presentation/ui/table-helpers";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { QueryState } from "@/shared/ui/QueryState";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useDebounceCallback,
} from "@devhop/ui";
import { useMemo, useState } from "react";

const STATUS_OPTIONS = [
  { label: "ທຸກສະຖານະ", value: "all" },
  { label: "ລໍຖ້າ", value: "pending" },
  { label: "ຢືນຢັນ", value: "confirmed" },
  { label: "ຈ່າຍແລ້ວ", value: "paid" },
  { label: "ຈັດສົ່ງ", value: "shipped" },
  { label: "ສົ່ງຮອດ", value: "delivered" },
  { label: "ຍົກເລີກ", value: "cancelled" },
];

const SOURCE_OPTIONS = [
  { label: "ທຸກແຫຼ່ງ", value: "all" },
  { label: "ຄອມເມັນ", value: "comment" },
  { label: "ຂໍ້ຄວາມ", value: "message" },
  { label: "ແອດມິນສ້າງ", value: "manual" },
];

const FEED_TYPE_OPTIONS = [
  { label: "ທຸກປະເພດ", value: "all" },
  { label: "ໂພສ", value: "post" },
  { label: "ໄລຟ໌", value: "live" },
];

const STATUS_LABEL: Record<string, string> = {
  pending: "ລໍຖ້າ",
  confirmed: "ຢືນຢັນ",
  paid: "ຈ່າຍແລ້ວ",
  shipped: "ຈັດສົ່ງ",
  delivered: "ສົ່ງຮອດ",
  cancelled: "ຍົກເລີກ",
};

const ADMIN_STATUS_LABEL: Record<string, string> = {
  pending: "ລໍຖ້າ",
  confirmed: "ຢືນຢັນ",
  paid: "ຈ່າຍແລ້ວ",
  managing: "ກຳລັງຈັດການ",
  packing: "ກຳລັງແພັກ",
  awaiting_carrier: "ລໍຖ້າຂົນສົ່ງ",
  processing: "ກຳລັງດຳເນີນການ",
  shipped: "ຈັດສົ່ງແລ້ວ",
  delivered: "ສົ່ງສຳເລັດ",
  cancelled: "ຍົກເລີກ",
  refunded: "ຄືນເງິນ",
};

function sourceLabel(source: CfOrderListItem["source"]) {
  if (source === "manual") return "ແອດມິນ";
  if (source === "message") return "ຂໍ້ຄວາມ";
  return "ຄອມເມັນ";
}

export function OrdersPage() {
  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [source, setSource] = useState("all");
  const [feedType, setFeedType] = useState("all");
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  const debouncedSearch = useDebounceCallback((value: string) => {
    setQ(value.trim());
  }, 350);

  const params = useMemo(
    () => ({
      q: q.trim() || undefined,
      status: status === "all" ? undefined : status,
      source: source === "all" ? undefined : source,
      feedType: feedType === "all" ? undefined : (feedType as "post" | "live"),
      limit: 100,
      offset: 0,
    }),
    [feedType, q, source, status],
  );

  const listQuery = useCfOrdersList(params);
  const countsQuery = useCfOrderStatusCounts();
  const detailQuery = useCfOrderDetail(selectedOrderId);

  const items = listQuery.data?.items ?? [];
  const counts = countsQuery.data?.counts;

  return (
    <>
      <Header />
      <Main>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h1 className="font-semibold text-2xl">ຕິດຕາມອໍເດີ</h1>
            <Badge variant="outline">CF Orders</Badge>
          </div>

          <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-7">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">ທັງໝົດ</CardTitle>
              </CardHeader>
              <CardContent className="font-semibold text-xl">
                {counts?.total ?? 0}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">ລໍຖ້າ</CardTitle>
              </CardHeader>
              <CardContent className="font-semibold text-xl">
                {counts?.pending ?? 0}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">ຢືນຢັນ</CardTitle>
              </CardHeader>
              <CardContent className="font-semibold text-xl">
                {counts?.confirmed ?? 0}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">ຈ່າຍແລ້ວ</CardTitle>
              </CardHeader>
              <CardContent className="font-semibold text-xl">
                {counts?.paid ?? 0}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">ຈັດສົ່ງ</CardTitle>
              </CardHeader>
              <CardContent className="font-semibold text-xl">
                {counts?.shipped ?? 0}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">ສົ່ງຮອດ</CardTitle>
              </CardHeader>
              <CardContent className="font-semibold text-xl">
                {counts?.delivered ?? 0}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">ຍົກເລີກ</CardTitle>
              </CardHeader>
              <CardContent className="font-semibold text-xl">
                {counts?.cancelled ?? 0}
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-3 lg:grid-cols-4">
            <Input
              placeholder="ຄົ້ນຫາ ລູກຄ້າ / CF / code"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                debouncedSearch(e.target.value);
              }}
            />
            <SimpleSelect
              value={status}
              onValueChange={setStatus}
              options={STATUS_OPTIONS}
            />
            <SimpleSelect
              value={source}
              onValueChange={setSource}
              options={SOURCE_OPTIONS}
            />
            <SimpleSelect
              value={feedType}
              onValueChange={setFeedType}
              options={FEED_TYPE_OPTIONS}
            />
          </div>

          <QueryState result={listQuery}>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ວັນທີ</TableHead>
                    <TableHead>ລູກຄ້າ</TableHead>
                    <TableHead>CF</TableHead>
                    <TableHead>ຈຳນວນ</TableHead>
                    <TableHead>ລາຄາ</TableHead>
                    <TableHead>ແຫຼ່ງ</TableHead>
                    <TableHead>ປະເພດ</TableHead>
                    <TableHead>ສະຖານະ</TableHead>
                    <TableHead>Admin code</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={10}
                        className="text-center text-muted-foreground"
                      >
                        ຍັງບໍ່ມີອໍເດີ
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((o) => (
                      <TableRow key={o.id}>
                        <TableCell>{formatDateTimeLocal(o.createdAt)}</TableCell>
                        <TableCell>{o.customerName ?? "-"}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {o.cfCodeUsed}
                        </TableCell>
                        <TableCell>{o.quantity}</TableCell>
                        <TableCell>{formatKip(o.totalPrice)}</TableCell>
                        <TableCell>{sourceLabel(o.source)}</TableCell>
                        <TableCell>{o.feedType === "live" ? "ໄລຟ໌" : "ໂພສ"}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {STATUS_LABEL[o.status] ?? o.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {o.shopOrderCode && o.adminOrderUrl ? (
                            <a
                              className="font-semibold text-primary underline underline-offset-4 hover:no-underline"
                              href={o.adminOrderUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {o.shopOrderCode}
                            </a>
                          ) : (
                            (o.shopOrderCode ?? "-")
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedOrderId(o.id)}
                          >
                            ລາຍລະອຽດ
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </QueryState>
        </div>
      </Main>

      <Dialog
        open={Boolean(selectedOrderId)}
        onOpenChange={(open) => {
          if (!open) setSelectedOrderId(null);
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>ລາຍລະອຽດອໍເດີ</DialogTitle>
          </DialogHeader>
          <QueryState result={detailQuery}>
            {detailQuery.data ? (
              <div className="space-y-4">
                <div className="grid gap-2 text-sm md:grid-cols-2">
                  <div>ລະຫັດ: {detailQuery.data.order.id}</div>
                  <div>
                    ສະຖານະ:{" "}
                    {STATUS_LABEL[detailQuery.data.order.status] ??
                      detailQuery.data.order.status}
                  </div>
                  <div>ລູກຄ້າ: {detailQuery.data.order.customerName ?? "-"}</div>
                  <div>CF: {detailQuery.data.order.cfCodeUsed}</div>
                  <div>ຈຳນວນ: {detailQuery.data.order.quantity}</div>
                  <div>ລາຄາ/ຊິ້ນ: {formatKip(detailQuery.data.order.unitPrice)}</div>
                  <div>ລວມ: {formatKip(detailQuery.data.order.totalPrice)}</div>
                  <div>
                    Admin code:{" "}
                    {detailQuery.data.order.shopOrderCode &&
                    detailQuery.data.order.adminOrderUrl ? (
                      <a
                        className="font-mono font-semibold text-primary underline underline-offset-4 hover:no-underline"
                        href={detailQuery.data.order.adminOrderUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {detailQuery.data.order.shopOrderCode}
                      </a>
                    ) : (
                      (detailQuery.data.order.shopOrderCode ?? "-")
                    )}
                  </div>
                </div>

                {detailQuery.data.adminShop ? (
                  <div className="space-y-2 rounded-lg border p-3">
                    <p className="font-medium text-sm">Admin POS</p>
                    <div className="grid gap-2 text-sm md:grid-cols-2">
                      <div>
                        ສະຖານະ Admin:{" "}
                        {ADMIN_STATUS_LABEL[detailQuery.data.adminShop.status] ??
                          detailQuery.data.adminShop.status}
                      </div>
                      <div>
                        ຍອດ Admin:{" "}
                        {formatKip(detailQuery.data.adminShop.finalTotalPrice)}
                      </div>
                      <div>
                        Tracking:{" "}
                        {detailQuery.data.adminShop.trackingNumber ?? "-"}
                      </div>
                      <div>
                        ອັບເດດ Admin:{" "}
                        {formatDateTimeLocal(detailQuery.data.adminShop.updatedAt)}
                      </div>
                    </div>
                    {detailQuery.data.adminTimeline.length > 0 ? (
                      <div className="space-y-1 pt-2">
                        {detailQuery.data.adminTimeline.map((step) => (
                          <div
                            key={step.key}
                            className="flex items-center justify-between text-xs"
                          >
                            <span
                              className={
                                step.done
                                  ? "text-foreground"
                                  : "text-muted-foreground"
                              }
                            >
                              {step.label}
                            </span>
                            <span className="text-muted-foreground">
                              {step.at
                                ? formatDateTimeLocal(step.at)
                                : step.done
                                  ? "-"
                                  : "ຍັງບໍ່ເຖິງ"}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    ຍັງບໍ່ເຊື່ອມ Admin shop order
                  </p>
                )}

                <div className="space-y-2">
                  <p className="font-medium text-sm">Timeline</p>
                  {detailQuery.data.activities.length === 0 ? (
                    <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີ activity</p>
                  ) : (
                    <div className="space-y-2">
                      {detailQuery.data.activities.map((a) => (
                        <div
                          key={a.id}
                          className="rounded border p-2 text-muted-foreground text-sm"
                        >
                          <div className="font-medium text-foreground text-xs">
                            {a.eventType}
                          </div>
                          <div>{a.description ?? "-"}</div>
                          <div>{formatDateTimeLocal(a.createdAt)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </QueryState>
        </DialogContent>
      </Dialog>
    </>
  );
}
